import { simulate } from 'api/simulation/simulateApi';
import { AuthenticationError, NetworkError, TimeoutError } from 'api/apiClient';
import { setSimulating } from 'store/simulation/reducer';
import { call, delay, put, takeLatest, select } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { withRetry } from 'store/sagaUtils';
import type { RootState } from 'store';
import type { SimulationRequestInterface } from 'store/simulation/types';
import { SIMULATE } from 'store/simulation/types';
import type { SimulationRequestActionInterface } from './actions';
import { setSimulationResult, simulationRequest } from './actions';
import { getIsOnline } from 'store/connection/selectors';

import { cleanUpRequestParams } from './cleanSimulationRequest';
import type { UnitInterface } from 'store/units/types';
import {
  EDIT_UNIT_FLOW,
  EDIT_UNIT_INFO,
  INSERT_ON_CONNECTION,
  REPLACE_UNIT,
  SET_UNIT_VALUE,
} from 'store/units/types';
import { getAutoSimulate } from 'store/settings/selectors';
import type { ConnectionInterface } from 'store/connections/types';
import {
  ADD_CONNECTION,
  EDIT_CONNECTION,
  REMOVE_CONNECTION,
  REMOVE_CONNECTIONS,
} from 'store/connections/types';
import type {
  EditUnitFlowActionInterface,
  EditUnitInfoActionInterface,
  InsertOnConnectionActionInterface,
  ReplaceUnitInterface,
} from 'store/units/sagaActions';
import type {
  AddConnectionActionInterface,
  EditConnectionActionInterface,
  RemoveConnectionActionInterface,
  RemoveConnectionsActionInterface,
} from 'store/connections/sagaActions';
import type { SetUnitValueAction } from 'store/units/reducer';
import { removeUnits } from 'store/units/reducer';
import { getUnits } from 'store/units/selectors';
import { UnitTypes } from 'unitData/types';
import { getConnections } from 'store/connections/selectors';
import { REDO, UNDO } from 'store/createHistoryReducer';
import { addMessage } from 'store/snackbar/reducer';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import { setRiskAssessmentSimulationSettings } from 'store/documentMetadata/reducer';
import { requestConnectivityCheck } from 'util/connectivityChecks';
import { InformationOnlyFields } from 'unitData/MultislopeScreen/MultislopeScreenData';

type PromiseReturnType<T> =
  T extends Promise<infer U>
    ? U
    : T extends (...args: never) => Promise<infer UFunc>
      ? UFunc
      : never;

function* simulateSaga(action: SimulationRequestActionInterface) {
  try {
    const { autoSimulate, onSuccess } = action.payload;

    let isOnline: boolean = yield select(getIsOnline);
    if (!isOnline) {
      return;
    }

    if (autoSimulate) {
      yield delay(1500);
      isOnline = yield select(getIsOnline);
      if (!isOnline) {
        return;
      }
    }

    yield put(setSimulating(true));

    const { units, ports } = yield select(
      (state: RootState) => state.unitConnGroup.present.unitState,
    );
    const { connections } = yield select(
      (state: RootState) => state.unitConnGroup.present.connectionState,
    );
    const { metricType, screenCalcMethod, flowsheetMetadata } = yield select(
      (state: RootState) => state.settingsState,
    );
    const applicationType = (
      flowsheetMetadata?.application ?? 'aggregate'
    ).toUpperCase();
    const simulateData = {
      units: units,
      portMapping: ports,
      connections: connections,
      globalConfig: {
        calcUnits: metricType.value,
        screenCalcMode: screenCalcMethod.value,
        applicationType,
      },
    };

    const cleanedUpRequestParams: SimulationRequestInterface =
      yield cleanUpRequestParams(simulateData);

    const result: PromiseReturnType<typeof simulate> = yield withRetry(() =>
      simulate(cleanedUpRequestParams),
    );

    yield put(setSimulationResult(result.parsedBody!)); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    yield put(setSimulating(false));
    if (onSuccess) onSuccess();
  } catch (err) {
    if (
      err instanceof NetworkError ||
      err instanceof TimeoutError ||
      err instanceof AuthenticationError
    ) {
      yield put(setSimulating(false));
      // Authentication failures are reported by the session recovery banner.
      if (err instanceof AuthenticationError) return;
      yield call(requestConnectivityCheck);
      yield put(
        addMessage(
          errorMessage('Simulation service unavailable. Please try again.'),
        ),
      );
      return;
    }

    Sentry.captureException(err, {
      tags: { saga: 'simulation' },
    });
    yield put(setSimulating(false));
    yield put(addMessage(errorMessage('Simulation failed')));
  }
}

type AutoSimulateActions =
  | SetUnitValueAction
  | EditUnitInfoActionInterface
  | EditUnitFlowActionInterface
  | ReplaceUnitInterface
  | InsertOnConnectionActionInterface
  | AddConnectionActionInterface
  | RemoveConnectionActionInterface
  | RemoveConnectionsActionInterface
  | EditConnectionActionInterface;

/**
 * This saga will trigger when any of the "AutoSimulateActions" are dispatched.
 * @param action the action that should trigger a simulation request
 */
function* autoSimulate(action: AutoSimulateActions) {
  const autoSimulate: boolean = yield select(getAutoSimulate);
  const units: UnitInterface[] = yield select(getUnits);
  const connections: ConnectionInterface[] = yield select(getConnections);

  if (action.type === EDIT_UNIT_INFO || action.type === SET_UNIT_VALUE) {
    const editedUnit = units.find((u) => u.key === action.payload.unitKey);
    if (
      editedUnit?.type === UnitTypes.FREE_TEXT_BOX ||
      editedUnit?.type === UnitTypes.PRINT_WINDOW
    ) {
      return;
    }
    if (
      action.type === SET_UNIT_VALUE &&
      action.payload.field === 'attachmentTool'
    ) {
      return;
    }
    if (
      action.type === SET_UNIT_VALUE &&
      editedUnit?.type === UnitTypes.MULTISLOPE_SCREEN &&
      typeof action.payload.field === 'string' &&
      InformationOnlyFields.has(action.payload.field)
    ) {
      // These fields are saved with the unit but do not affect calculations.
      return;
    }
  }

  if (action.type === REPLACE_UNIT) {
    const { key } = action.payload;
    const isConnected = connections.find(
      (connection) => connection.from === key || connection.to === key,
    );
    if (!isConnected) {
      return;
    }
  }

  if (autoSimulate) {
    const isOnline: boolean = yield select(getIsOnline);
    if (isOnline) {
      yield put(simulationRequest());
    }
  }
}

/**
 * This saga triggers when risk assessment simulation settings are changed.
 * If auto-simulate is enabled, it will trigger a simulation.
 */
function* autoSimulateOnRiskSettingsChange() {
  const autoSimulate: boolean = yield select(getAutoSimulate);
  if (autoSimulate) {
    const isOnline: boolean = yield select(getIsOnline);
    if (isOnline) {
      yield put(simulationRequest());
    }
  }
}

function* simulationSagas() {
  yield takeLatest(SET_UNIT_VALUE, autoSimulate);
  yield takeLatest(REPLACE_UNIT, autoSimulate);
  yield takeLatest(EDIT_UNIT_INFO, autoSimulate);
  yield takeLatest(EDIT_UNIT_FLOW, autoSimulate);
  yield takeLatest(INSERT_ON_CONNECTION, autoSimulate);
  yield takeLatest(ADD_CONNECTION, autoSimulate);
  yield takeLatest(REMOVE_CONNECTION, autoSimulate);
  yield takeLatest(REMOVE_CONNECTIONS, autoSimulate);
  yield takeLatest(EDIT_CONNECTION, autoSimulate);
  yield takeLatest(UNDO, autoSimulate);
  yield takeLatest(REDO, autoSimulate);
  yield takeLatest(removeUnits.type, autoSimulate);
  yield takeLatest(
    setRiskAssessmentSimulationSettings.type,
    autoSimulateOnRiskSettingsChange,
  );

  yield takeLatest(SIMULATE, simulateSaga);
}

export default simulationSagas;
